export const FREE_STRIPE_COUPON_IDS = ['E1zJTCPH', 's53ck8fO']

export const STRIPE_SUBSCRIPTION_ID_PATTERN = /^sub_/

/** Deal Sauce Stripe account suffix (separate from Housefolios billing). */
export const DEALSAUCE_STRIPE_ACCOUNT_SUFFIX = 'G0Nqc9OHil'

export function isStripeSubscriptionId(id) {
  return typeof id === 'string' && STRIPE_SUBSCRIPTION_ID_PATTERN.test(id)
}

export function isDealsauceSubscription(subscription) {
  if (!subscription || typeof subscription !== 'object') return false
  if (subscription.dealsaucePlanId) return true
  if (
    typeof subscription.id === 'string' &&
    subscription.id.includes(DEALSAUCE_STRIPE_ACCOUNT_SUFFIX)
  ) {
    return true
  }
  return false
}

const MEMBER_GROUP_LABELS = {
  housefolios: 'Housefolios',
  '': 'Housefolios',
  pep: 'PEP',
  pacemorby: 'Pace Morby',
  astroflip: 'AstroFlip',
  'pep-duplicates': 'PEP Duplicates',
  paulmccomas: 'Paul McComas',
  privatemoneyclub: 'Private Money Club',
  gorillacapital: 'Gorilla Capital',
  investarters: 'Investarters',
  nextlevel: 'Next Level',
  roitk: 'ROITK',
  brdevelop: 'BR Develop',
}

export function getMemberGroupKey(member) {
  const key = (member || '').trim()
  return key || 'housefolios'
}

export function getMemberGroupLabel(member) {
  const key = getMemberGroupKey(member)
  return MEMBER_GROUP_LABELS[key] || key
}

function getFirstSubscriptionItem(subscription) {
  const items = subscription?.items?.data || subscription?.items || []
  return Array.isArray(items) ? items[0] : null
}

export function isPausedSubscription(subscription) {
  return Boolean(subscription?.pause_collection?.behavior)
}

export function getSubscriptionCouponIds(subscription) {
  if (!subscription || typeof subscription !== 'object') return []

  const ids = []
  const legacyId = subscription.discount?.coupon?.id
  if (legacyId) ids.push(legacyId)

  for (const discount of subscription.discounts || []) {
    const id =
      discount?.source?.coupon?.id ||
      discount?.coupon?.id ||
      (typeof discount?.coupon === 'string' ? discount.coupon : null)
    if (id) ids.push(id)
  }

  return ids
}

export function hasFreeStripeCoupon(subscription) {
  return getSubscriptionCouponIds(subscription).some((id) =>
    FREE_STRIPE_COUPON_IDS.includes(id),
  )
}

export function isRevenueEligibleSubscription(subscription) {
  if (!subscription || typeof subscription !== 'object') return false
  if (subscription.disabled) return false
  if (!isStripeSubscriptionId(subscription.id)) return false
  if (isDealsauceSubscription(subscription)) return false
  if (isPausedSubscription(subscription)) return false
  if (hasFreeStripeCoupon(subscription)) return false
  return true
}

export function getSubscriptionPlanNickname(subscription) {
  if (!subscription) return ''
  const item = getFirstSubscriptionItem(subscription)
  return (
    subscription.plan?.nickname ||
    item?.price?.nickname ||
    item?.plan?.nickname ||
    subscription.nickname ||
    ''
  )
}

export function getSubscriptionInterval(subscription) {
  const nickname = getSubscriptionPlanNickname(subscription).toLowerCase()
  if (nickname.includes('yearly') || nickname.includes('annual')) {
    return 'year'
  }
  if (nickname.includes('monthly')) return 'month'

  const item = getFirstSubscriptionItem(subscription)
  return (
    subscription.plan?.interval ||
    item?.price?.recurring?.interval ||
    item?.plan?.interval ||
    null
  )
}

export function getSubscriptionAmountCents(subscription) {
  if (!subscription) return 0
  const item = getFirstSubscriptionItem(subscription)
  const amount =
    subscription.plan?.amount ??
    item?.price?.unit_amount ??
    item?.plan?.amount ??
    0
  const qty = item?.quantity || subscription.quantity || 1
  const total = Number(amount) * Number(qty)
  return Number.isFinite(total) ? total : 0
}

function getCouponFromDiscount(discount) {
  if (!discount || typeof discount !== 'object') return null
  if (discount.coupon && typeof discount.coupon === 'object') {
    return discount.coupon
  }
  if (discount.source?.coupon && typeof discount.source.coupon === 'object') {
    return discount.source.coupon
  }
  return null
}

function isDiscountCurrentlyActive(discount, now = new Date()) {
  if (!discount || typeof discount !== 'object') return false
  if (discount.end == null || discount.end === '') return true
  const end = formatSubscriptionUnixDate(discount.end)
  if (!end) return true
  return end.getTime() > now.getTime()
}

/** Active Stripe coupon objects currently reducing this subscription. */
export function getActiveSubscriptionCoupons(subscription, now = new Date()) {
  if (!subscription || typeof subscription !== 'object') return []

  const coupons = []
  const seen = new Set()

  const addCoupon = (discount) => {
    if (!isDiscountCurrentlyActive(discount, now)) return
    const coupon = getCouponFromDiscount(discount)
    if (!coupon?.id || seen.has(coupon.id)) return
    seen.add(coupon.id)
    coupons.push(coupon)
  }

  addCoupon(subscription.discount)

  for (const discount of subscription.discounts || []) {
    if (typeof discount === 'string') continue
    addCoupon(discount)
  }

  return coupons
}

export function applyCouponsToAmountCents(amountCents, coupons) {
  let remaining = Number(amountCents)
  if (!Number.isFinite(remaining) || remaining <= 0) return 0

  for (const coupon of coupons || []) {
    if (!coupon || typeof coupon !== 'object') continue
    if (coupon.percent_off != null) {
      remaining -= (remaining * Number(coupon.percent_off)) / 100
      continue
    }
    if (coupon.amount_off != null) {
      remaining -= Number(coupon.amount_off)
    }
  }

  return Math.max(0, Math.round(remaining))
}

/** List price minus any currently active Stripe coupons. */
export function getSubscriptionDiscountedAmountCents(
  subscription,
  now = new Date(),
) {
  const listCents = getSubscriptionAmountCents(subscription)
  const coupons = getActiveSubscriptionCoupons(subscription, now)
  return applyCouponsToAmountCents(listCents, coupons)
}

export function formatSubscriptionUnixDate(unixSeconds) {
  if (unixSeconds == null || unixSeconds === '') return null
  const n = Number(unixSeconds)
  if (!Number.isFinite(n) || n <= 0) return null
  const ms = n > 1e12 ? n : n * 1000
  const date = new Date(ms)
  return Number.isNaN(date.getTime()) ? null : date
}

export function formatSubscriptionDateString(unixSeconds) {
  const date = formatSubscriptionUnixDate(unixSeconds)
  return date ? date.toLocaleString() : null
}

export function formatSubscriptionShortDate(unixSeconds) {
  const date = formatSubscriptionUnixDate(unixSeconds)
  if (!date) return null
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/** Stripe Billing moved current_period_end onto subscription items. */
export function getSubscriptionPeriodEndSeconds(subscription) {
  const item = getFirstSubscriptionItem(subscription)
  const candidates = [
    subscription?.current_period_end,
    item?.current_period_end,
  ]
  for (const raw of candidates) {
    const n = Number(raw)
    if (Number.isFinite(n) && n > 0) return n
  }
  return null
}

export function getNextBillingDateLabel(subscription) {
  const seconds = getSubscriptionPeriodEndSeconds(subscription)
  return seconds ? formatSubscriptionShortDate(seconds) : null
}

function formatShortDate(date) {
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/** Compact trial label for reports tables: NA, date range, or ended. */
export function getTrialDisplayLabel(subscription, now = new Date()) {
  if (!subscription?.trial_start && !subscription?.trial_end) {
    return 'NA'
  }

  const start = formatSubscriptionUnixDate(subscription.trial_start)
  const end = formatSubscriptionUnixDate(subscription.trial_end)

  if (!start && !end) return 'NA'

  if (start && end) {
    const startLabel = start.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    })
    const endLabel = formatShortDate(end)
    if (end.getTime() > now.getTime()) {
      return `${startLabel} – ${endLabel}`
    }
    return `Ended ${endLabel}`
  }

  if (end) {
    const endLabel = formatShortDate(end)
    if (end.getTime() > now.getTime()) {
      return `Ends ${endLabel}`
    }
    return `Ended ${endLabel}`
  }

  return 'NA'
}

export function isActiveSubscriptionPeriod(subscription, now = new Date()) {
  const seconds = getSubscriptionPeriodEndSeconds(subscription)
  if (!seconds) return true
  const end = formatSubscriptionUnixDate(seconds)
  if (!end) return true
  return now.getTime() < end.getTime()
}

export function getActiveEligibleSubscriptions(org, now = new Date()) {
  const subscriptionsArray = Array.isArray(org?.subscriptions)
    ? org.subscriptions
    : org?.subscriptions
      ? [org.subscriptions]
      : []

  return subscriptionsArray.filter(
    (subscription) =>
      isRevenueEligibleSubscription(subscription) &&
      isActiveSubscriptionPeriod(subscription, now),
  )
}

export function getSubscriptionMrrDollars(subscription, now = new Date()) {
  if (!isRevenueEligibleSubscription(subscription)) return 0

  const amountCents = getSubscriptionDiscountedAmountCents(subscription, now)
  if (!amountCents) return 0

  const nickname = getSubscriptionPlanNickname(subscription)
  const interval = getSubscriptionInterval(subscription)
  const nicknameLc = nickname.toLowerCase()

  if (
    interval === 'month' ||
    nicknameLc.includes('monthly') ||
    nickname === 'enterprise-affiliate'
  ) {
    return amountCents / 100
  }

  if (
    interval === 'year' ||
    nicknameLc.includes('yearly') ||
    nicknameLc.includes('annual')
  ) {
    return amountCents / 100 / 12
  }

  return 0
}

export const MEMBER_GROUP_FILTER_ALL = 'all'
export const MEMBER_GROUP_FILTER_HOUSEFOLIOS = 'housefolios'

export function orgMatchesMemberGroupFilter(org, filterKey) {
  if (!filterKey || filterKey === MEMBER_GROUP_FILTER_ALL) return true
  return getMemberGroupKey(org.member) === filterKey
}

export function buildMemberGroupFilterOptions(orgs) {
  const groups = summarizeBillableOrgsByMemberGroup(orgs)
  const otherGroups = groups
    .filter((group) => group.key !== MEMBER_GROUP_FILTER_HOUSEFOLIOS)
    .map((group) => ({ value: group.key, label: group.label  }))
    .sort((a, b) => a.label.localeCompare(b.label))

  return [
    {
      value: MEMBER_GROUP_FILTER_HOUSEFOLIOS,
      label: 'Housefolios (core)',
    },
    ...otherGroups,
    { value: MEMBER_GROUP_FILTER_ALL, label: 'All member groups' },
  ]
}

export function summarizeBillableOrgsByMemberGroup(orgs) {
  const groups = {}

  for (const org of orgs) {
    const eligibleSubs = getActiveEligibleSubscriptions(org)
    if (!eligibleSubs.length) continue

    const key = getMemberGroupKey(org.member)
    const label = getMemberGroupLabel(org.member)
    if (!groups[key]) {
      groups[key] = { key, label, orgCount: 0, mrr: 0 }
    }

    groups[key].orgCount += 1
    groups[key].mrr += getSubscriptionMrrDollars(eligibleSubs[0])
  }

  return Object.values(groups).sort((a, b) => b.mrr - a.mrr)
}

export const FREE_ACCESS_REASON = {
  FREE_COUPON: 'free_coupon',
  COMPED_SUBSCRIPTION: 'comped_subscription',
  FREE_ACCOUNT: 'free_account',
}

export const FREE_ACCESS_REASON_LABELS = {
  [FREE_ACCESS_REASON.FREE_COUPON]: '100% off coupon',
  [FREE_ACCESS_REASON.COMPED_SUBSCRIPTION]: 'Comped / faux subscription',
  [FREE_ACCESS_REASON.FREE_ACCOUNT]: 'Free account flag',
}

function getOrgSubscriptionsArray(org) {
  if (Array.isArray(org?.subscriptions)) return org.subscriptions
  if (org?.subscriptions) return [org.subscriptions]
  return []
}

export function isHundredPercentOffCoupon(coupon) {
  if (!coupon) return false
  if (typeof coupon === 'string') {
    return FREE_STRIPE_COUPON_IDS.includes(coupon)
  }
  if (typeof coupon !== 'object') return false
  if (FREE_STRIPE_COUPON_IDS.includes(coupon.id)) return true
  return Number(coupon.percent_off) === 100
}

export function subscriptionHasHundredPercentOff(subscription) {
  if (!subscription || typeof subscription !== 'object') return false
  if (subscription.disabled) return false
  if (isHundredPercentOffCoupon(subscription.coupon)) return true
  if (isHundredPercentOffCoupon(subscription.discount?.coupon)) return true
  for (const discount of subscription.discounts || []) {
    if (typeof discount === 'string') continue
    if (isHundredPercentOffCoupon(getCouponFromDiscount(discount))) {
      return true
    }
  }
  return false
}

/** Non-Stripe “subscription” that still unlocks the app (e.g. daniel@housefolios.com). */
export function isCompedFauxSubscription(subscription) {
  if (!subscription || typeof subscription !== 'object') return false
  if (subscription.disabled) return false
  if (isDealsauceSubscription(subscription)) return false
  if (isStripeSubscriptionId(subscription.id)) return false
  return Boolean(
    subscription.planId ||
      subscription.nickname ||
      subscription.plan?.nickname ||
      subscription.plan?.id,
  )
}

/**
 * Orgs that can use the product without paying Housefolios Stripe:
 * 100% coupons, freeAccount flag, or comped/faux subscriptions.
 * Partner-provisioned member seats (pep/pacemorby/etc.) are excluded unless
 * they also have a free coupon or freeAccount flag.
 */
export function getFreeAccessReasons(org) {
  if (!org || org.disabled) return []

  const reasons = []
  const subscriptions = getOrgSubscriptionsArray(org)

  if (org.freeAccount === true) {
    reasons.push(FREE_ACCESS_REASON.FREE_ACCOUNT)
  }

  if (subscriptions.some(subscriptionHasHundredPercentOff)) {
    reasons.push(FREE_ACCESS_REASON.FREE_COUPON)
  }

  const hasCompedFaux = subscriptions.some(isCompedFauxSubscription)
  const isCoreOrInternal =
    getMemberGroupKey(org.member) === MEMBER_GROUP_FILTER_HOUSEFOLIOS ||
    (typeof org.email === 'string' &&
      org.email.toLowerCase().endsWith('@housefolios.com'))

  if (hasCompedFaux && isCoreOrInternal) {
    reasons.push(FREE_ACCESS_REASON.COMPED_SUBSCRIPTION)
  }

  return reasons
}

export function isFreeAccessOrg(org) {
  return getFreeAccessReasons(org).length > 0
}

export function getFreeAccessReasonLabels(org) {
  return getFreeAccessReasons(org).map(
    (reason) => FREE_ACCESS_REASON_LABELS[reason] || reason,
  )
}

/** Mongo prefilter for free-access candidates (still post-filter with isFreeAccessOrg). */
export const FREE_ACCESS_ORG_QUERY = {
  disabled: { $ne: true },
  $or: [
    { freeAccount: true },
    {
      'subscriptions.discount.coupon.id': {
        $in: FREE_STRIPE_COUPON_IDS,
      },
    },
    { 'subscriptions.discount.coupon.percent_off': 100 },
    { 'subscriptions.coupon': { $in: FREE_STRIPE_COUPON_IDS } },
    {
      subscriptions: {
        $elemMatch: {
          disabled: { $ne: true },
          $or: [
            { id: { $exists: false } },
            { id: null },
            { id: { $not: { $regex: /^sub_/ } } },
          ],
        },
      },
    },
  ],
}
